/**
 * PFAP Hub — Guide Mode (Phase 1) context provider + hook.
 *
 * Single source of truth for:
 *   - guide_mode_enabled (per-user, persisted on the backend)
 *   - dismissed_guides   (per-user, persisted on the backend)
 *   - sessionDismissed   (in-memory only; cleared on hard reload)
 *
 * The provider lazily fetches preferences once after the user is
 * authenticated. Backend failures are non-fatal — we fall back to safe
 * defaults (`guide_mode_enabled=true`, no dismissals) so a missing
 * `/api/guide/preferences` never blocks the app from rendering.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  guide_mode_enabled: true,
  dismissed_guides: [],
  guide_versions_seen: {},
};

const GuideModeContext = createContext({
  ...DEFAULTS,
  loading: true,
  sessionDismissed: new Set(),
  setEnabled: () => {},
  dismissGuide: () => {},
  dismissForSession: () => {},
  isDismissed: () => false,
});

export const GuideModeProvider = ({ children }) => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [sessionDismissed, setSessionDismissed] = useState(() => new Set());
  const fetchedFor = useRef(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  }), []);

  // Lazy-fetch preferences once per logged-in user. Logout resets
  // local state to defaults so the next login starts clean.
  useEffect(() => {
    if (!user) {
      setPrefs(DEFAULTS);
      setLoading(false);
      fetchedFor.current = null;
      setSessionDismissed(new Set());
      return;
    }
    const uid = user.id || user.user_id || user.email;
    if (fetchedFor.current === uid) return;
    fetchedFor.current = uid;
    setLoading(true);
    axios.get(`${API}/api/guide/preferences`, { headers: headers() })
      .then((r) => {
        const d = r.data || {};
        setPrefs({
          guide_mode_enabled: d.guide_mode_enabled !== false,
          dismissed_guides: Array.isArray(d.dismissed_guides) ? d.dismissed_guides : [],
          guide_versions_seen: d.guide_versions_seen || {},
        });
      })
      .catch(() => {
        // Backend down or 404 — safe defaults; do NOT cascade-logout.
        setPrefs(DEFAULTS);
      })
      .finally(() => setLoading(false));
  }, [user, headers]);

  const setEnabled = useCallback(async (next) => {
    setPrefs((p) => ({ ...p, guide_mode_enabled: !!next }));
    try {
      await axios.patch(
        `${API}/api/guide/preferences`,
        { guide_mode_enabled: !!next },
        { headers: headers() },
      );
    } catch (_e) {
      // Soft failure — local state already updated.
    }
  }, [headers]);

  const dismissGuide = useCallback(async (guideId, version = 1) => {
    if (!guideId) return;
    setPrefs((p) => ({
      ...p,
      dismissed_guides: p.dismissed_guides.includes(guideId)
        ? p.dismissed_guides
        : [...p.dismissed_guides, guideId],
      guide_versions_seen: { ...p.guide_versions_seen, [guideId]: version },
    }));
    try {
      await axios.post(
        `${API}/api/guide/dismiss`,
        { guide_id: guideId, version },
        { headers: headers() },
      );
    } catch (_e) {
      // Soft failure.
    }
  }, [headers]);

  const dismissForSession = useCallback((guideId) => {
    if (!guideId) return;
    setSessionDismissed((s) => {
      const n = new Set(s);
      n.add(guideId);
      return n;
    });
  }, []);

  const isDismissed = useCallback(
    (guideId) => prefs.dismissed_guides.includes(guideId) || sessionDismissed.has(guideId),
    [prefs.dismissed_guides, sessionDismissed],
  );

  const value = useMemo(() => ({
    ...prefs,
    loading,
    sessionDismissed,
    setEnabled,
    dismissGuide,
    dismissForSession,
    isDismissed,
  }), [prefs, loading, sessionDismissed, setEnabled, dismissGuide, dismissForSession, isDismissed]);

  return (
    <GuideModeContext.Provider value={value}>{children}</GuideModeContext.Provider>
  );
};

export const useGuideMode = () => useContext(GuideModeContext);
